/**
 * Shared cover image resolver for event announcement screens
 * (livestreams, live rooms, offline events).
 *
 * The backend sometimes returns `null`, the literal string `"null"`, or URLs
 * that end in `/null` — these should be treated as missing. The resolver
 * walks a list of candidate paths and returns the first usable one after
 * running it through `getFileUrl`.
 *
 * Also exposes a branded fallback so we never render a pure-black hero on the
 * "modern digital magazine" announcement pages.
 *
 * CORE-2004 — the fallback is a bundled asset, not a third-party photo host:
 * the external one times out from Russia, so the "branded" fallback was a blank
 * hero there. The URI comes from expo-asset and must be rendered as is — never
 * passed through `getFileUrl` (it would rewrite a relative asset path to S3).
 */

import { Asset } from 'expo-asset';
import { getFileUrl } from '../services/api';

const EVENT_COVER_FALLBACK_ASSET = require('../../assets/lives/meditation.jpg');

export const eventCoverFallbackUri = (): string =>
  Asset.fromModule(EVENT_COVER_FALLBACK_ASSET).uri;

export const isUsableCoverUrl = (u: string | null | undefined): u is string => {
  if (!u) return false;
  if (u === 'null' || u === 'undefined') return false;
  if (u.endsWith('/null') || u.endsWith('null')) return false;
  if (u.includes('/null?') || u.includes('/undefined?')) return false;
  if (u.includes('undefined')) return false;
  return true;
};

/**
 * Resolve the best-available cover URL from a priority list of raw paths.
 * Each path is passed through `getFileUrl` (resolves S3 proxy on web) and
 * filtered via `isUsableCoverUrl`. If everything is missing, returns the
 * branded bundled fallback.
 */
export const resolveEventCover = (
  candidates: Array<string | null | undefined>,
  fallback?: string,
): string => {
  for (const path of candidates) {
    if (!isUsableCoverUrl(path)) continue;
    const resolved = getFileUrl(path);
    if (isUsableCoverUrl(resolved)) return resolved;
  }
  return fallback ?? eventCoverFallbackUri();
};
